/**
 * PageHelpBlock Component
 * Collapsible help section for bottom of each page
 * Shows what the page does, why it matters, key metrics, and how to use
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, Lightbulb, AlertTriangle, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export const PageHelpBlock = ({ 
  pageKey, 
  content,
  className,
  defaultExpanded = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!content) return null;

  return (
    <div 
      className={cn(
        "mt-8 border-t border-border/50 pt-4",
        className
      )}
      data-testid={`page-help-${pageKey}`}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left group"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2 text-muted-foreground group-hover:text-foreground transition-colors">
          <Info size={16} />
          <span className="text-sm font-medium">Comprendre cette page</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-xs">
            {isExpanded ? 'Réduire' : 'Développer'}
          </span>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* What & Why */}
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide mb-1">
                Ce que fait cette page
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {content.what}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide mb-1">
                Pourquoi c'est important
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {content.why}
              </p>
            </div>
          </div>

          {/* Key Metrics */}
          <div>
            <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide mb-2">
              Métriques Clés
            </h4>
            <ul className="space-y-1.5">
              {content.metrics?.slice(0, 4).map((metric, idx) => (
                <li key={idx} className="text-sm">
                  <span className="font-medium text-foreground/90">{metric.name}:</span>{' '}
                  <span className="text-muted-foreground">{metric.description}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* How to Use */}
          <div>
            <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Lightbulb size={12} />
              Comment utiliser
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {content.howToUse}
            </p>
          </div>

          {/* Common Mistakes */}
          {content.commonMistakes && content.commonMistakes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-foreground/80 uppercase tracking-wide mb-2 flex items-center gap-1">
                <AlertTriangle size={12} className="text-amber-500" />
                Erreurs à éviter
              </h4>
              <ul className="space-y-1">
                {content.commonMistakes.map((mistake, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-1.5">
                    <span className="text-amber-500 mt-1">•</span>
                    <span>{mistake}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {isExpanded && content.relatedModules && (
        <div className="mt-4 pt-3 border-t border-border/30 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Liens:</span>
          {content.relatedModules.map((module, idx) => (
            <a
              key={idx}
              href={`/help#${module}`}
              className="text-xs text-primary hover:underline flex items-center gap-0.5"
            >
              {module.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              <ArrowRight size={10} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default PageHelpBlock;
